import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RvHookupIcon from "@mui/icons-material/RvHookup";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Box, Divider, Skeleton, Typography } from "@mui/material";
import React from "react";
import { useGetDispatchersQuery } from "../../../api/employeesApi";
import { useGetTrailerByIdQuery } from "../../../api/trailersApi";
import { useGetVehicleByIdQuery } from "../../../api/vehiclesApi";
import {
  formatDispatcherDisplayLabel,
  formatTrailerDisplayLabel,
  formatVehicleDisplayLabel,
} from "../auditEntityRegistry";
import {
  AssignmentTimelineSegment,
} from "../utils/driverAssignmentTimelineUtils";

export const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, py: 0.25 }}>
    <Box sx={{ color: "text.disabled", display: "flex", flexShrink: 0 }}>{icon}</Box>
    <Typography color="text.secondary" sx={{ fontSize: "0.75rem", flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
      {value || "-"}
    </Typography>
  </Box>
);

const VehicleDetails: React.FC<{ entityId: number; open: boolean }> = ({ entityId, open }) => {
  const { data: vehicle, isFetching } = useGetVehicleByIdQuery(entityId, { skip: !open });

  if (isFetching && !vehicle) {
    return <Skeleton variant="rounded" height={48} />;
  }

  return (
    <>
      <InfoRow icon={<LocalShippingIcon sx={{ fontSize: 14 }} />} label="Name" value={formatVehicleDisplayLabel(vehicle)} />
      <InfoRow icon={<FingerprintIcon sx={{ fontSize: 14 }} />} label="VIN" value={vehicle?.vin} />
      <InfoRow icon={<BadgeIcon sx={{ fontSize: 14 }} />} label="Reg." value={vehicle?.registrationNumber} />
      {vehicle?.make && (
        <InfoRow icon={<LocalShippingIcon sx={{ fontSize: 14 }} />} label="Make" value={[vehicle.make, vehicle.model].filter(Boolean).join(" ")} />
      )}
      <InfoRow icon={<CheckCircleIcon sx={{ fontSize: 14 }} color={vehicle?.lastStatus ? "success" : "disabled"} />} label="Status" value={vehicle?.lastStatus} />
    </>
  );
};

const TrailerDetails: React.FC<{ entityId: number; open: boolean }> = ({ entityId, open }) => {
  const { data: trailer, isFetching } = useGetTrailerByIdQuery(entityId, { skip: !open });

  if (isFetching && !trailer) {
    return <Skeleton variant="rounded" height={48} />;
  }

  return (
    <>
      <InfoRow icon={<RvHookupIcon sx={{ fontSize: 14 }} />} label="Unit #" value={formatTrailerDisplayLabel(trailer)} />
      <InfoRow icon={<FingerprintIcon sx={{ fontSize: 14 }} />} label="VIN" value={trailer?.vin} />
      <InfoRow icon={<RvHookupIcon sx={{ fontSize: 14 }} />} label="Type" value={trailer?.trailerType} />
      {trailer?.make && (
        <InfoRow icon={<RvHookupIcon sx={{ fontSize: 14 }} />} label="Make" value={[trailer.make, trailer.model].filter(Boolean).join(" ")} />
      )}
      <InfoRow icon={<CheckCircleIcon sx={{ fontSize: 14 }} color={trailer?.trailerStatus ? "success" : "disabled"} />} label="Status" value={trailer?.trailerStatus} />
    </>
  );
};

const DispatcherDetails: React.FC<{ entityId: number; open: boolean }> = ({ entityId, open }) => {
  const { data: dispatchers, isFetching } = useGetDispatchersQuery(undefined, { skip: !open });
  const dispatcher = dispatchers?.find((item) => item.id === entityId);

  if (isFetching && !dispatcher) {
    return <Skeleton variant="rounded" height={36} />;
  }

  return (
    <>
      <InfoRow icon={<SupportAgentIcon sx={{ fontSize: 14 }} />} label="Name" value={formatDispatcherDisplayLabel(dispatcher)} />
      <InfoRow icon={<EmailIcon sx={{ fontSize: 14 }} />} label="Email" value={dispatcher?.email} />
      <InfoRow icon={<CheckCircleIcon sx={{ fontSize: 14 }} color={dispatcher?.isActive ? "success" : "disabled"} />} label="Active" value={dispatcher ? (dispatcher.isActive ? "Yes" : "No") : undefined} />
    </>
  );
};

export const formatPopoverDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const formatDuration = (startAt: string, endAt?: string) => {
  const start = new Date(startAt);
  const end = endAt ? new Date(endAt) : new Date();

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
    days += prevMonth.getDate();
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  if (months > 0) parts.push(`${months} ${months === 1 ? "month" : "months"}`);
  if (days > 0 || parts.length === 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);

  return parts.join(" and ");
};

interface StateBoxPopoverProps {
  segment: AssignmentTimelineSegment;
  open: boolean;
}

const StateBoxPopover: React.FC<StateBoxPopoverProps> = ({ segment, open }) => {
  if (segment.kind === "unassigned") {
    return (
      <Box sx={{ maxWidth: 260, p: 1.5 }}>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.75 }}>
          No asset assigned
        </Typography>
        <InfoRow
          icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
          label="From"
          value={formatPopoverDateTime(segment.startAt)}
        />
        {segment.endAt && (
          <InfoRow
            icon={<EventIcon sx={{ fontSize: 14 }} />}
            label="Until"
            value={formatPopoverDateTime(segment.endAt)}
          />
        )}
        {!segment.endAt && (
          <InfoRow
            icon={<EventIcon sx={{ fontSize: 14 }} />}
            label="Until"
            value={<Typography component="span" sx={{ color: "warning.main", fontSize: "0.75rem", fontWeight: 600 }}>Present</Typography>}
          />
        )}
        <InfoRow
          icon={<HourglassBottomIcon sx={{ fontSize: 14 }} />}
          label="Duration"
          value={formatDuration(segment.startAt, segment.endAt)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 260, p: 1.5 }}>
      {/* Period */}
      <InfoRow
        icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
        label="From"
        value={formatPopoverDateTime(segment.startAt)}
      />
      {segment.endAt ? (
        <InfoRow
          icon={<EventIcon sx={{ fontSize: 14 }} />}
          label="Until"
          value={formatPopoverDateTime(segment.endAt)}
        />
      ) : (
        <InfoRow
          icon={<EventIcon sx={{ fontSize: 14 }} />}
          label="Until"
          value={<Typography component="span" sx={{ color: "success.main", fontSize: "0.75rem", fontWeight: 600 }}>Current</Typography>}
        />
      )}
      <InfoRow
        icon={<HourglassBottomIcon sx={{ fontSize: 14 }} />}
        label="Duration"
        value={formatDuration(segment.startAt, segment.endAt)}
      />

      {/* Asset details */}
      {segment.entityType && segment.entityId && (
        <>
          <Divider sx={{ my: 1 }} />
          {segment.entityType === "Vehicle" && (
            <VehicleDetails entityId={segment.entityId} open={open} />
          )}
          {segment.entityType === "Trailer" && (
            <TrailerDetails entityId={segment.entityId} open={open} />
          )}
          {segment.entityType === "Employee" && (
            <DispatcherDetails entityId={segment.entityId} open={open} />
          )}
        </>
      )}
    </Box>
  );
};

export default StateBoxPopover;
